import { WorkerProfileField } from 'types/profileCustomFields';

/**
 * Extracts custom field codes from a field configuration object
 * @param fieldConfig - Object mapping WorkerProfileField enum values to their config
 * @returns Array of custom field codes (enum values) used in the configuration
 */
export const extractCustomFieldCodes = (fieldConfig: Partial<Record<WorkerProfileField, any>>): string[] => {
  return Object.keys(fieldConfig).map(key => key as string);
};

/**
 * Maps backend error keys to frontend form field names using the existing field configuration
 * @param backendErrors - Error object from backend with potentially nested keys
 * @param fieldConfig - Field configuration that includes formField and reduxField mappings
 * @returns Mapped errors object with frontend form field names as keys
 */
export const mapBackendErrorsToFormFields = (
  backendErrors: Record<string, any>,
  fieldConfig: Partial<Record<WorkerProfileField, any>>,
): Record<string, { message: string; type: string }> => {
  const mappedErrors: Record<string, { message: string; type: string }> = {};

  // Create a reverse mapping: backend key -> form field
  const backendKeyToFormField: Record<string, string> = {};

  Object.entries(fieldConfig).forEach(([workerProfileField, config]) => {
    const formField = config.formField;

    // Map the WorkerProfileField enum value (e.g., "WorkerHomeAddressLine1")
    backendKeyToFormField[workerProfileField] = formField;

    // Map the reduxField (backend API key, e.g., "address.line1")
    if (config.reduxField) {
      backendKeyToFormField[config.reduxField] = formField;
    }
  });

  // Map the backend errors to form field names
  Object.entries(backendErrors).forEach(([backendKey, errorValue]) => {
    // Try to find the matching form field, fallback to original key if not found
    const formField = backendKeyToFormField[backendKey] || backendKey;
    const errorMessage = Array.isArray(errorValue) ? errorValue.join(' ') : errorValue;

    mappedErrors[formField] = {
      type: 'backendError',
      message: errorMessage,
    };
  });

  return mappedErrors;
};
